import { normalizeConsent } from './cookie-consent.js';
import { captureFirstTouch } from './attribution.js';
import { recordPageView, recordVisitEvent } from './visit-metrics.js';
import { updateMetaPixel, trackMetaLead } from './meta-pixel.js';

// One loader owns analytics; the existing GTM container is the deployment default.
export const analyticsConfig = {
  provider: process.env.NEXT_PUBLIC_ANALYTICS_PROVIDER || 'gtm',
  gaId: process.env.NEXT_PUBLIC_GA_ID || 'G-LZXVG6DE4B',
  gtmId: process.env.NEXT_PUBLIC_GTM_ID || 'GTM-MPBWM6C',
};
const productionHosts = /^(www\.)?palmate\.ai$/i;
// Tags are limited to production inside GTM; testweb and previews share the same container.
export function resolveEnvironment(hostname) {
  return productionHosts.test(String(hostname || '')) ? 'production' : 'test';
}
function currentHostname() {
  const { hostname, href } = window.location || {};
  if (hostname) return hostname;
  try { return new URL(href).hostname; } catch { return ''; }
}
// Custom events go to the dataLayer for GTM or straight to gtag for the direct GA4 provider.
export function trackEvent(name, params = {}, config = analyticsConfig) {
  if (typeof window === 'undefined' || !name) return;
  window.dataLayer = window.dataLayer || [];
  if (name === 'generate_lead' || name === 'newsletter_subscribe') { try { recordVisitEvent(name, params.form_type); } catch {} }
  try { trackMetaLead(name, params); } catch {}
  if (config.provider === 'gtm') { window.dataLayer.push({ event: name, ...params }); return; }
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  window.gtag('event', name, params);
}
export function updateAnalytics(value, config = analyticsConfig) {
  const { analytics, marketing } = normalizeConsent(value);
  const useGtm = config.provider === 'gtm';
  window.dataLayer = window.dataLayer || [];
  window.gtag = window.gtag || function () { window.dataLayer.push(arguments); };
  if (!window.__palmateEnvironment) {
    // Pushed once and before gtm.js so every tag can read the environment.
    const hostname = currentHostname();
    window.__palmateEnvironment = resolveEnvironment(hostname);
    window.dataLayer.push({ palmate_environment: window.__palmateEnvironment, palmate_hostname: hostname });
  }
  // The cookieless first-party counter runs for every visitor, before any consent gate.
  try { recordPageView(); } catch {}
  try {
    captureFirstTouch({ href: window.location?.href, referrer: document.referrer, storage: window.localStorage, sessionStorage: window.sessionStorage, persistent: analytics || marketing });
  } catch {}
  window['ga-disable-' + config.gaId] = !analytics;
  const changed = window.__palmateConsent?.analytics !== analytics || window.__palmateConsent?.marketing !== marketing;
  window.gtag('consent', window.__palmateConsentInitialized ? 'update' : 'default', {
    analytics_storage: analytics ? 'granted' : 'denied',
    ad_storage: marketing ? 'granted' : 'denied',
    ad_user_data: marketing ? 'granted' : 'denied',
    ad_personalization: marketing ? 'granted' : 'denied',
  });
  window.__palmateConsentInitialized = true;
  window.__palmateConsent = { analytics, marketing };
  try { updateMetaPixel(marketing); } catch {}
  window.gtag('set', { allow_google_signals: marketing, allow_ad_personalization_signals: marketing });
  if (useGtm && changed) window.dataLayer.push({ event: 'palmate_consent_update', palmate_analytics_consent: analytics, palmate_marketing_consent: marketing });
  if (!analytics) window.__palmateLastPage = null;
  // GTM can serve either category. Direct GA4 loads only for analytics consent.
  if (!analytics && !(useGtm && marketing)) return;
  if (!document.querySelector('script[data-palmate-analytics]')) {
    const script = document.createElement('script');
    script.async = true; script.dataset.palmateAnalytics = 'true';
    if (useGtm) {
      window.dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
      script.src = 'https://www.googletagmanager.com/gtm.js?id=' + config.gtmId;
    } else {
      window.gtag('js', new Date());
      window.gtag('config', config.gaId, { send_page_view: false });
      script.src = 'https://www.googletagmanager.com/gtag/js?id=' + config.gaId;
    }
    document.head.appendChild(script);
  }
  if (!analytics) return;
  const pageLocation = window.location.href;
  if (window.__palmateLastPage !== pageLocation) {
    const page = { page_location: pageLocation, page_title: document.title, page_referrer: window.__palmateLastPage || document.referrer };
    window.__palmateLastPage = pageLocation;
    if (useGtm) window.dataLayer.push({ event: 'palmate_page_view', ...page });
    else window.gtag('event', 'page_view', page);
  }
}
